import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth-context";
import { supabase } from "@/integrations/supabase/client";
import { SiteHeader } from "@/components/site-header";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ExternalLink, CheckCircle2, AlertCircle, Loader2, Copy, Check } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/points/checkout/$orderId")({
  head: () => ({ meta: [{ title: "Shopier ile Öde — JUËN" }] }),
  component: PointsShopierPaymentPage,
});

interface PackageRow {
  id: string;
  name: string;
  points: number;
  price_kurus: number;
  shopier_payment_url: string | null;
}

interface OrderRow {
  id: string;
  user_id: string;
  status: string;
  points: number;
  price_kurus: number;
  package_id: string;
}

function PointsShopierPaymentPage() {
  const { orderId: packageId } = Route.useParams();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [pkg, setPkg] = useState<PackageRow | null>(null);
  const [order, setOrder] = useState<OrderRow | null>(null);
  const [pageLoading, setPageLoading] = useState(true);
  const [opened, setOpened] = useState(false);
  const [codeCopied, setCodeCopied] = useState(false);

  const orderCode = order ? order.id.slice(0, 8).toUpperCase() : "";

  const handleCopyCode = async () => {
    if (!orderCode) return;
    try {
      await navigator.clipboard.writeText(orderCode);
      setCodeCopied(true);
      toast.success("Sipariş kodu kopyalandı");
      setTimeout(() => setCodeCopied(false), 2000);
    } catch {
      toast.error("Kopyalanamadı");
    }
  };

  useEffect(() => {
    if (!loading && !user) void navigate({ to: "/login" });
  }, [loading, user, navigate]);

  useEffect(() => {
    if (!user) return;
    void (async () => {
      setPageLoading(true);
      try {
        const { data: packageData, error: packageError } = await supabase
          .from("point_packages")
          .select("id,name,points,price_kurus,shopier_payment_url")
          .eq("id", packageId)
          .eq("is_active", true)
          .maybeSingle();

        if (packageError || !packageData) {
          toast.error("Paket bulunamadı");
          void navigate({ to: "/points" });
          return;
        }

        const loadedPackage = packageData as PackageRow;
        setPkg(loadedPackage);

        if (!loadedPackage.shopier_payment_url) {
          setPageLoading(false);
          return;
        }

        const { data: pendingOrder } = await supabase
          .from("point_package_orders")
          .select("*")
          .eq("user_id", user.id)
          .eq("package_id", packageId)
          .eq("status", "pending")
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle();

        if (pendingOrder) {
          setOrder(pendingOrder as OrderRow);
          setPageLoading(false);
          return;
        }

        const { data: created, error: createError } = await supabase.rpc("create_point_package_order", {
          _package_id: packageId,
        });
        if (createError) throw createError;

        const createdOrderId = (created as { order_id?: string } | null)?.order_id;
        if (!createdOrderId) throw new Error("Sipariş oluşturulamadı");

        const { data: createdOrder } = await supabase
          .from("point_package_orders")
          .select("*")
          .eq("id", createdOrderId)
          .maybeSingle();

        setOrder(
          (createdOrder as OrderRow | null) ?? {
            id: createdOrderId,
            user_id: user.id,
            status: "pending",
            points: loadedPackage.points,
            price_kurus: loadedPackage.price_kurus,
            package_id: loadedPackage.id,
          },
        );
      } catch (err) {
        const message = err instanceof Error ? err.message : "Sipariş oluşturulamadı";
        toast.error(message);
      } finally {
        setPageLoading(false);
      }
    })();
  }, [packageId, user, navigate]);

  useEffect(() => {
    if (!order || order.status === "paid") return;
    const iv = setInterval(async () => {
      const { data } = await supabase
        .from("point_package_orders")
        .select("status")
        .eq("id", order.id)
        .maybeSingle();
      if (data?.status === "paid") {
        setOrder((o) => (o ? { ...o, status: "paid" } : o));
        toast.success("Ödemen alındı, J hesabına yüklendi 🎉");
      }
    }, 5000);
    return () => clearInterval(iv);
  }, [order]);

  const handleOpenShopier = () => {
    if (!pkg?.shopier_payment_url || !order) return;
    const normalized = /^https?:\/\//i.test(pkg.shopier_payment_url)
      ? pkg.shopier_payment_url
      : `https://${pkg.shopier_payment_url.replace(/^\/+/, "")}`;
    window.open(normalized, "_blank", "noopener,noreferrer");
    setOpened(true);
  };

  if (loading || !user || pageLoading) {
    return (
      <div className="min-h-screen bg-background">
        <SiteHeader />
        <div className="mx-auto max-w-2xl px-4 py-16 text-center text-muted-foreground">
          <Loader2 className="mx-auto h-6 w-6 animate-spin" />
        </div>
      </div>
    );
  }

  if (!pkg) return null;

  if (order?.status === "paid") {
    return (
      <div className="min-h-screen bg-background">
        <SiteHeader />
        <div className="mx-auto max-w-2xl px-4 py-16 text-center">
          <CheckCircle2 className="mx-auto h-12 w-12 text-primary" />
          <h1 className="mt-4 text-2xl font-bold">Ödemen tamamlandı</h1>
          <p className="mt-2 text-muted-foreground">{order.points} J hesabına yüklendi.</p>
          <Button asChild className="mt-6">
            <Link to="/points">J Cüzdanım</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <div className="mx-auto max-w-2xl px-4 py-10">
        <Link
          to="/points"
          className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" /> J Cüzdanıma dön
        </Link>

        <h1 className="text-3xl font-bold tracking-tight">Shopier ile Öde</h1>
        <p className="mt-2 text-muted-foreground">
          {pkg.name} · {pkg.points.toLocaleString("tr-TR")} J ({(pkg.price_kurus / 100).toLocaleString("tr-TR")} ₺)
        </p>

        <div className="mt-6 rounded-2xl border border-amber-500/30 bg-amber-500/5 p-5">
          <div className="flex items-start gap-3">
            <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-500" />
            <div className="space-y-2 text-sm">
              <p className="font-semibold text-foreground">Nasıl ödeme yapılır?</p>
              <ol className="list-decimal space-y-1 pl-4 text-muted-foreground">
                <li>Aşağıdaki <strong>"Shopier ile satın al"</strong> butonuna bas — yeni sekmede açılır.</li>
                <li>Shopier sayfasında <strong>"Hemen Al"</strong> butonuna tıkla; ardından kart bilgilerini girerek ödemeyi güvenli şekilde tamamla.</li>
                {order && (
                  <li>
                    Ödeme açıklamasına <strong>sipariş kodunu yaz:</strong>{" "}
                    <span className="inline-flex items-center gap-1.5 align-middle">
                      <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-foreground">{orderCode}</code>
                      <button
                        type="button"
                        onClick={handleCopyCode}
                        className="inline-flex h-6 w-6 items-center justify-center rounded border border-border bg-background transition-colors hover:bg-muted"
                        aria-label="Sipariş kodunu kopyala"
                      >
                        {codeCopied ? (
                          <Check className="h-3.5 w-3.5 text-primary" />
                        ) : (
                          <Copy className="h-3.5 w-3.5 text-muted-foreground" />
                        )}
                      </button>
                    </span>
                  </li>
                )}
                <li>Ödeme onaylandıktan sonra J otomatik olarak hesabına yüklenir.</li>
              </ol>
            </div>
          </div>
        </div>

        <div className="mt-6 rounded-2xl border border-border bg-card p-4 shadow-card">
          <div className="flex items-center justify-between gap-4">
            <div className="min-w-0 flex-1">
              <div className="font-semibold">{pkg.name}</div>
              <div className="text-sm text-muted-foreground">
                {pkg.points.toLocaleString("tr-TR")} J · {(pkg.price_kurus / 100).toLocaleString("tr-TR")} ₺
              </div>
            </div>
          </div>

          {!pkg.shopier_payment_url || !order ? (
            <div className="mt-4 rounded-xl border border-destructive/30 bg-destructive/5 p-4 text-sm">
              Bu paket için Shopier ödeme linki tanımlı değil. Lütfen yöneticiyle iletişime geç.
            </div>
          ) : (
            <Button
              type="button"
              onClick={handleOpenShopier}
              variant={opened ? "outline" : "default"}
              className="mt-4 w-full"
              size="lg"
            >
              {opened ? (
                <>
                  <CheckCircle2 className="mr-1.5 h-4 w-4" /> Tekrar aç
                </>
              ) : (
                <>
                  Shopier ile satın al <ExternalLink className="ml-1.5 h-4 w-4" />
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
